import { ReactElement, useMemo } from 'react';
import { HubShell } from '@/components/Hub/HubShell';
import { TabOption } from '@/components/Timeline/TimelineTabs';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { FinancingTab } from './tabs/FinancingTab';
import { InquiryTab } from './tabs/InquiryTab';
import { NoShowTab } from './tabs/NoShowTab';
import { PreConsultationTab } from './tabs/PreConsultationTab';
import { PostConsultationTab } from './tabs/PostConsultationTab';
import { ToScheduleTab } from './tabs/ToScheduleTab';
import { WonTab } from './tabs/WonTab';

export type IntakeTab =
  | 'inquiry'
  | 'no-show'
  | 'pre-consultation'
  | 'post-consultation'
  | 'financing'
  | 'to-schedule'
  | 'won';
const DEFAULT_TAB: IntakeTab = 'inquiry';

// The prototype's sub-tabs carry no icons.
const TAB_OPTIONS: TabOption<IntakeTab>[] = [
  { label: 'Inquiry', icon: null, value: 'inquiry' },
  { label: 'Consultation no-show', icon: null, value: 'no-show' },
  { label: 'Pre-consultation', icon: null, value: 'pre-consultation' },
  { label: 'Post-consultation', icon: null, value: 'post-consultation' },
  { label: 'Financing and approval', icon: null, value: 'financing' },
  { label: 'To schedule', icon: null, value: 'to-schedule' },
  { label: 'Won', icon: null, value: 'won' },
];

/** Both read the payment status: being on To schedule says a patient is paid or approved. */
const PAYMENTS_TABS: ReadonlySet<IntakeTab> = new Set(['to-schedule', 'won']);

export function IntakeHub({ defaultTab = DEFAULT_TAB }: { defaultTab?: IntakeTab }): ReactElement {
  const { canViewPayments } = useCurrentUser().currentUserPermissions;
  // Won reads the payments ledger and To schedule the payment status, so both are hidden without payments access.
  const tabs = useMemo(
    () => (canViewPayments ? TAB_OPTIONS : TAB_OPTIONS.filter((tab) => !PAYMENTS_TABS.has(tab.value))),
    [canViewPayments]
  );

  return (
    <HubShell tabs={tabs} defaultTab={defaultTab}>
      {(activeTab) => (
        <>
          {activeTab === 'inquiry' && <InquiryTab />}
          {activeTab === 'no-show' && <NoShowTab />}
          {activeTab === 'pre-consultation' && <PreConsultationTab />}
          {activeTab === 'post-consultation' && <PostConsultationTab />}
          {activeTab === 'financing' && <FinancingTab />}
          {activeTab === 'to-schedule' && <ToScheduleTab />}
          {activeTab === 'won' && <WonTab />}
        </>
      )}
    </HubShell>
  );
}
